import { useTranslation } from "react-i18next";
import { useDates } from "../../../../hooks/useDates";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { useFormatNumber } from "../../../../hooks/useFormatNumber";
import { Box, Grid, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

type ColumnType = GridColDef & {
    field?: string,
    headerName?: string,
    headerClassName?: string,
    flex?: number,
    type?: string,
    headerAlign?: string,
    valueGetter?: (params: GridValueGetterParams) => any;
};

export const useProjectsColumns = () => {
    const { TimeConverter } = useDates();
    const { setFormatNumberFromTable } = useFormatNumber();
    const { t } = useTranslation("global");

    const createColumnsFromData = async (data: any) => {
        const fixedColumns = [
            { field: 'project_name', headerName: t("projects.fields.project_name"), headerClassName: 'header-grid-table', flex: 1 },
            { field: 'project_type_name', headerName: t("projects.fields.project_type"), headerClassName: 'header-grid-table', flex: 1 },
            {
                field: 'comercial_name', headerName: t("projects.fields.customer"), headerClassName: 'header-grid-table', flex: 1,
                valueGetter: (params: any) => `${(params.row.customer_identifier) ? params.row.customer_identifier : "XXX"} - ${(params.row.comercial_name) ? params.row.comercial_name : params.row.business_name}`,
            },
            { field: 'user_name', headerName: t("projects.fields.project_manager"), headerClassName: 'header-grid-table', flex: 1 },
            {
                field: 'project_start_date', headerName: t("projects.fields.project_start_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
                valueGetter: (params: any) => TimeConverter(params.value),
            },
            {
                field: 'project_end_date', headerName: t("projects.fields.project_end_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
                valueGetter: (params: any) => TimeConverter(params.value),
            },
            {
                field: 'project_user_closed', headerName: t("projects.fields.project_user_closed"), headerClassName: 'header-grid-table', flex: 1,
            },
            {
                field: 'project_user_date_closed', headerName: t("projects.fields.project_user_date_closed"), headerClassName: 'header-grid-table', type: "date", flex: 1,
                valueGetter: (params: any) => params.value ? TimeConverter(params.value) : undefined,
            },
            {
                field: 'subtotal_sales', headerName: t("reportprojects.fields.subtotal_sales"), headerClassName: 'header-grid-table', flex: 1, minWidth: 200, type: 'number',
                valueGetter(params: any) {
                    return setFormatNumberFromTable(params.value ?? 0);
                },
            },
            {
                field: 'subtotal_purchases', headerName: t("reportprojects.fields.subtotal_purchases"), headerClassName: 'header-grid-table', flex: 1, minWidth: 200, type: 'number',
                valueGetter(params: any) {
                    return setFormatNumberFromTable(params.value ?? 0);
                },
            },
            {
                field: 'subtotal_amount', headerName: t("reportprojects.fields.subtotal_amount"), headerClassName: 'header-grid-table', flex: 1, minWidth: 300, type: 'number',
                renderCell: (params: any) => {
                    return (
                        <Box className="d-flex flex-row">
                            <Typography variant='body2' sx={{ paddingRight: 0.5 }}>
                                {setFormatNumberFromTable(params.value ?? 0)}
                            </Typography>
                            <Typography
                                variant='body2'
                                color={`${((params.value) /
                                    params.row.subtotal_purchases) *
                                    100 <
                                    0
                                    ? "red"
                                    : "green"
                                    }`}
                            >
                                {"%"}
                                {isNaN(
                                    Math.round(
                                        ((params.value) /
                                            params.row.subtotal_purchases) *
                                        100 *
                                        100
                                    ) / 100
                                )
                                    ? 0
                                    : Math.round(
                                        ((params.value) /
                                            params.row.subtotal_purchases) *
                                        100 *
                                        100
                                    ) / 100}
                            </Typography>
                            <Typography
                                variant='body2'
                                color={`${((params.value) /
                                    params.row.subtotal_purchases) *
                                    100 <
                                    0
                                    ? "red"
                                    : "green"
                                    }`}
                            >
                                {(isNaN(
                                    ((params.value) /
                                        params.row.subtotal_purchases) *
                                    100
                                )
                                    ? 0
                                    : ((params.value) /
                                        params.row.subtotal_purchases) *
                                    100) < 0 ? (
                                    <ArrowDownwardIcon />
                                ) : (
                                    <ArrowUpwardIcon />
                                )}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                field: 'total_sales', headerName: t("reportprojects.fields.total_sales"), headerClassName: 'header-grid-table', flex: 1, minWidth: 200, type: 'number',
                valueGetter(params: any) {
                    return setFormatNumberFromTable(params.value ?? 0);
                },
            },
            {
                field: 'total_purchases', headerName: t("reportprojects.fields.total_purchases"), headerClassName: 'header-grid-table', flex: 1, minWidth: 200, type: 'number',
                valueGetter(params: any) {
                    return setFormatNumberFromTable(params.value ?? 0);
                },
            },
            {
                field: 'total_amount', headerName: t("reportprojects.fields.total_amount"), headerClassName: 'header-grid-table', flex: 1, minWidth: 300, type: 'number',
                renderCell: (params: any) => {
                    return (
                        <Box className="d-flex flex-row">
                            <Typography variant='body2' sx={{ paddingRight: 0.5 }}>
                                {setFormatNumberFromTable(params.value ?? 0)}
                            </Typography>
                            <Typography
                                variant='body2'
                                color={`${((params.value) /
                                    params.row.total_purchases) *
                                    100 <
                                    0
                                    ? "red"
                                    : "green"
                                    }`}
                            >
                                {"%"}
                                {isNaN(
                                    Math.round(
                                        ((params.value) /
                                            params.row.total_purchases) *
                                        100 *
                                        100
                                    ) / 100
                                )
                                    ? 0
                                    : Math.round(
                                        ((params.value) /
                                            params.row.total_purchases) *
                                        100 *
                                        100
                                    ) / 100}
                            </Typography>
                            <Typography
                                variant='body2'
                                color={`${((params.value) /
                                    params.row.total_purchases) *
                                    100 <
                                    0
                                    ? "red"
                                    : "green"
                                    }`}
                            >
                                {(isNaN(
                                    ((params.value) /
                                        params.row.total_purchases) *
                                    100
                                )
                                    ? 0
                                    : ((params.value) /
                                        params.row.total_purchases) *
                                    100) < 0 ? (
                                    <ArrowDownwardIcon />
                                ) : (
                                    <ArrowUpwardIcon />
                                )}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
                valueGetter: (params: any) => TimeConverter(params.value),
            },
            { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
            {
                field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
                valueGetter: (params: any) => TimeConverter(params.value),
            },
        ];

        const dynamicColumns: ColumnType[] = [];
        if (data.length > 0) {
            const sampleRow = data[0];

            Object.keys(sampleRow).forEach(key => {
                if (key.startsWith('value_')) {
                    const columnKey = key.replace('value_', '');
                    const typeKey = `type_${columnKey}`;
                    const type = sampleRow[typeKey] ? sampleRow[typeKey].toLowerCase() : 'string';

                    const column: ColumnType = {
                        field: key,
                        headerName: columnKey,
                        headerClassName: 'header-grid-table',
                        flex: 1,
                        type: type,
                        headerAlign: 'left',
                        align: 'left'
                    };

                    if (type === 'date') {
                        column.valueGetter = (params) => TimeConverter(params.value);
                    }

                    dynamicColumns.push(column);
                }
            });

            const columnMap = dynamicColumns.reduce((map, column) => {
                if (column.headerName) {
                    map[column.headerName] = column;
                }
                return map;
            }, {} as { [key: string]: ColumnType });
        }
        return [fixedColumns, dynamicColumns];
    };

    return {
        createColumnsFromData,
    };
};